import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { PermissionApi } from "../../api/PermissionApi";
import type { CreateRole } from "../../generated/dto/CreateRole";
import type { CreateRoleAssignment } from "../../generated/dto/CreateRoleAssignment";
import type { CreateRolePermission } from "../../generated/dto/CreateRolePermission";
import type { CreateScopes } from "../../generated/dto/CreateScopes";
import type { CreateUserPermission } from "../../generated/dto/CreateUserPermission";
import type { DeleteRoleAssignmentStaff } from "../../generated/dto/DeleteRoleAssignmentStaff";
import type { DeleteRolePermissionStaff } from "../../generated/dto/DeleteRolePermissionStaff";
import type { DeleteRoleStaff } from "../../generated/dto/DeleteRoleStaff";
import type { DeleteScopesStaff } from "../../generated/dto/DeleteScopesStaff";
import type { DeleteUserPermissionStaff } from "../../generated/dto/DeleteUserPermissionStaff";
import type { RoleAssignmentFetchOptions } from "../../generated/dto/RoleAssignmentFetchOptions";
import type { RoleAssignmentStaff } from "../../generated/dto/RoleAssignmentStaff";
import type { RoleFetchOptions } from "../../generated/dto/RoleFetchOptions";
import type { RolePermissionFetchOptions } from "../../generated/dto/RolePermissionFetchOptions";
import type { RolePermissionStaff } from "../../generated/dto/RolePermissionStaff";
import type { RoleStaff } from "../../generated/dto/RoleStaff";
import type { ScopesFetchOptions } from "../../generated/dto/ScopesFetchOptions";
import type { ScopesStaff } from "../../generated/dto/ScopesStaff";
import type { UpdateRole } from "../../generated/dto/UpdateRole";
import type { UpdateRoleAssignment } from "../../generated/dto/UpdateRoleAssignment";
import type { UpdateRolePermission } from "../../generated/dto/UpdateRolePermission";
import type { UpdateScopes } from "../../generated/dto/UpdateScopes";
import type { UpdateUserPermission } from "../../generated/dto/UpdateUserPermission";
import type { UserPermissionFetchOptions } from "../../generated/dto/UserPermissionFetchOptions";
import type { UserPermissionStaff } from "../../generated/dto/UserPermissionStaff";
import type { UserStaff } from "../../generated/dto/UserStaff";
import type { UserStaffFetchOptions } from "../../generated/dto/UserStaffFetchOptions";

export class PermissionApiClient implements PermissionApi {
  constructor(private readonly client = useClient()) {}

  async getScopesStaff(id: string, options?: ScopesFetchOptions): Promise<ScopesStaff> {
    const { data } = await this.client.get<unknown>(`/scopes/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.ScopesFetchOptions") });
    return deserializeCbiDto<ScopesStaff>(data, "generated.dto.ScopesStaff");
  }

  async getRoleStaff(id: number, options?: RoleFetchOptions): Promise<RoleStaff> {
    const { data } = await this.client.get<unknown>(`/role/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.RoleFetchOptions") });
    return deserializeCbiDto<RoleStaff>(data, "generated.dto.RoleStaff");
  }

  async getRolePermissionStaff(id: number, options?: RolePermissionFetchOptions): Promise<RolePermissionStaff> {
    const { data } = await this.client.get<unknown>(`/rolepermission/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.RolePermissionFetchOptions") });
    return deserializeCbiDto<RolePermissionStaff>(data, "generated.dto.RolePermissionStaff");
  }

  async getRoleAssignmentStaff(id: number, options?: RoleAssignmentFetchOptions): Promise<RoleAssignmentStaff> {
    const { data } = await this.client.get<unknown>(`/roleassignment/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.RoleAssignmentFetchOptions") });
    return deserializeCbiDto<RoleAssignmentStaff>(data, "generated.dto.RoleAssignmentStaff");
  }

  async getUserPermissionStaff(id: number, options?: UserPermissionFetchOptions): Promise<UserPermissionStaff> {
    const { data } = await this.client.get<unknown>(`/userpermission/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.UserPermissionFetchOptions") });
    return deserializeCbiDto<UserPermissionStaff>(data, "generated.dto.UserPermissionStaff");
  }

  async getAllScopesStaffs(options?: ScopesFetchOptions): Promise<Array<ScopesStaff>> {
    const { data } = await this.client.get<unknown>(`/scopes/staff/all`, { params: serializeCbiDto(options, "generated.dto.ScopesFetchOptions") });
    return deserializeCbiDto<Array<ScopesStaff>>(data, "generated.dto.ScopesStaff");
  }

  async getAllRoleStaffs(options?: RoleFetchOptions): Promise<Array<RoleStaff>> {
    const { data } = await this.client.get<unknown>(`/role/staff/all`, { params: serializeCbiDto(options, "generated.dto.RoleFetchOptions") });
    return deserializeCbiDto<Array<RoleStaff>>(data, "generated.dto.RoleStaff");
  }

  async createScopesStaff(source: CreateScopes): Promise<ScopesStaff> {
    const { data } = await this.client.post<unknown>(`/scopes/staff`, serializeCbiDto(source, "generated.dto.CreateScopes"));
    return deserializeCbiDto<ScopesStaff>(data, "generated.dto.ScopesStaff");
  }

  async createRoleStaff(source: CreateRole): Promise<RoleStaff> {
    const { data } = await this.client.post<unknown>(`/role/staff`, serializeCbiDto(source, "generated.dto.CreateRole"));
    return deserializeCbiDto<RoleStaff>(data, "generated.dto.RoleStaff");
  }

  async createRolePermissionStaff(source: CreateRolePermission): Promise<RolePermissionStaff> {
    const { data } = await this.client.post<unknown>(`/rolepermission/staff`, serializeCbiDto(source, "generated.dto.CreateRolePermission"));
    return deserializeCbiDto<RolePermissionStaff>(data, "generated.dto.RolePermissionStaff");
  }

  async createRoleAssignmentStaff(source: CreateRoleAssignment): Promise<RoleAssignmentStaff> {
    const { data } = await this.client.post<unknown>(`/roleassignment/staff`, serializeCbiDto(source, "generated.dto.CreateRoleAssignment"));
    return deserializeCbiDto<RoleAssignmentStaff>(data, "generated.dto.RoleAssignmentStaff");
  }

  async createUserPermissionStaff(source: CreateUserPermission): Promise<UserPermissionStaff> {
    const { data } = await this.client.post<unknown>(`/userpermission/staff`, serializeCbiDto(source, "generated.dto.CreateUserPermission"));
    return deserializeCbiDto<UserPermissionStaff>(data, "generated.dto.UserPermissionStaff");
  }

  async updateScopesStaff(source: UpdateScopes): Promise<ScopesStaff> {
    const { data } = await this.client.put<unknown>(`/scopes/staff/${source.scopeId}`, serializeCbiDto(source, "generated.dto.UpdateScopes"));
    return deserializeCbiDto<ScopesStaff>(data, "generated.dto.ScopesStaff");
  }

  async updateRoleStaff(source: UpdateRole): Promise<RoleStaff> {
    const { data } = await this.client.put<unknown>(`/role/staff/${source.roleId}`, serializeCbiDto(source, "generated.dto.UpdateRole"));
    return deserializeCbiDto<RoleStaff>(data, "generated.dto.RoleStaff");
  }

  async updateRolePermissionStaff(source: UpdateRolePermission): Promise<RolePermissionStaff> {
    const { data } = await this.client.put<unknown>(`/rolepermission/staff/${source.rolePermissionId}`, serializeCbiDto(source, "generated.dto.UpdateRolePermission"));
    return deserializeCbiDto<RolePermissionStaff>(data, "generated.dto.RolePermissionStaff");
  }

  async updateRoleAssignmentStaff(source: UpdateRoleAssignment): Promise<RoleAssignmentStaff> {
    const { data } = await this.client.put<unknown>(`/roleassignment/staff/${source.roleAssignmentId}`, serializeCbiDto(source, "generated.dto.UpdateRoleAssignment"));
    return deserializeCbiDto<RoleAssignmentStaff>(data, "generated.dto.RoleAssignmentStaff");
  }

  async updateUserPermissionStaff(source: UpdateUserPermission): Promise<UserPermissionStaff> {
    const { data } = await this.client.put<unknown>(`/userpermission/staff/${source.userPermissionId}`, serializeCbiDto(source, "generated.dto.UpdateUserPermission"));
    return deserializeCbiDto<UserPermissionStaff>(data, "generated.dto.UserPermissionStaff");
  }

  async deleteScopesStaff(source: DeleteScopesStaff): Promise<void> {
    await this.client.delete(`/scopes/staff/${source.scopeId}`);
  }

  async deleteRoleStaff(source: DeleteRoleStaff): Promise<void> {
    await this.client.delete(`/role/staff/${source.roleId}`);
  }

  async deleteRolePermissionStaff(source: DeleteRolePermissionStaff): Promise<void> {
    await this.client.delete(`/rolepermission/staff/${source.rolePermissionId}`);
  }

  async deleteRoleAssignmentStaff(source: DeleteRoleAssignmentStaff): Promise<void> {
    await this.client.delete(`/roleassignment/staff/${source.roleAssignmentId}`);
  }

  async deleteUserPermissionStaff(source: DeleteUserPermissionStaff): Promise<void> {
    await this.client.delete(`/userpermission/staff/${source.userPermissionId}`);
  }

  async getScopesStaffRolePermissions(id: string, options?: RolePermissionFetchOptions): Promise<Array<RolePermissionStaff>> {
    const { data } = await this.client.get<unknown>(`/scopes/staff/${id}/rolepermissions`, { params: serializeCbiDto(options, "generated.dto.RolePermissionFetchOptions") });
    return deserializeCbiDto<Array<RolePermissionStaff>>(data, "generated.dto.RolePermissionStaff");
  }

  async getScopesStaffUserPermissions(id: string, options?: UserPermissionFetchOptions): Promise<Array<UserPermissionStaff>> {
    const { data } = await this.client.get<unknown>(`/scopes/staff/${id}/userpermissions`, { params: serializeCbiDto(options, "generated.dto.UserPermissionFetchOptions") });
    return deserializeCbiDto<Array<UserPermissionStaff>>(data, "generated.dto.UserPermissionStaff");
  }

  async getRoleStaffPermissions(id: number, options?: RolePermissionFetchOptions): Promise<Array<RolePermissionStaff>> {
    const { data } = await this.client.get<unknown>(`/role/staff/${id}/permissions`, { params: serializeCbiDto(options, "generated.dto.RolePermissionFetchOptions") });
    return deserializeCbiDto<Array<RolePermissionStaff>>(data, "generated.dto.RolePermissionStaff");
  }

  async getRoleStaffAssignments(id: number, options?: RoleAssignmentFetchOptions): Promise<Array<RoleAssignmentStaff>> {
    const { data } = await this.client.get<unknown>(`/role/staff/${id}/assignments`, { params: serializeCbiDto(options, "generated.dto.RoleAssignmentFetchOptions") });
    return deserializeCbiDto<Array<RoleAssignmentStaff>>(data, "generated.dto.RoleAssignmentStaff");
  }

  async getRolePermissionStaffRoleId(id: number, options?: RoleFetchOptions): Promise<RoleStaff> {
    const { data } = await this.client.get<unknown>(`/rolepermission/staff/${id}/roleid`, { params: serializeCbiDto(options, "generated.dto.RoleFetchOptions") });
    return deserializeCbiDto<RoleStaff>(data, "generated.dto.RoleStaff");
  }

  async getRolePermissionStaffScopeId(id: number, options?: ScopesFetchOptions): Promise<ScopesStaff> {
    const { data } = await this.client.get<unknown>(`/rolepermission/staff/${id}/scopeid`, { params: serializeCbiDto(options, "generated.dto.ScopesFetchOptions") });
    return deserializeCbiDto<ScopesStaff>(data, "generated.dto.ScopesStaff");
  }

  async getRoleAssignmentStaffUserId(id: number, options?: UserStaffFetchOptions): Promise<UserStaff> {
    const { data } = await this.client.get<unknown>(`/roleassignment/staff/${id}/userid`, { params: serializeCbiDto(options, "generated.dto.UserStaffFetchOptions") });
    return deserializeCbiDto<UserStaff>(data, "generated.dto.UserStaff");
  }

  async getRoleAssignmentStaffRoleId(id: number, options?: RoleFetchOptions): Promise<RoleStaff> {
    const { data } = await this.client.get<unknown>(`/roleassignment/staff/${id}/roleid`, { params: serializeCbiDto(options, "generated.dto.RoleFetchOptions") });
    return deserializeCbiDto<RoleStaff>(data, "generated.dto.RoleStaff");
  }

  async getRoleAssignmentStaffAssignedBy(id: number, options?: UserStaffFetchOptions): Promise<UserStaff> {
    const { data } = await this.client.get<unknown>(`/roleassignment/staff/${id}/assignedby`, { params: serializeCbiDto(options, "generated.dto.UserStaffFetchOptions") });
    return deserializeCbiDto<UserStaff>(data, "generated.dto.UserStaff");
  }

  async getUserPermissionStaffUserId(id: number, options?: UserStaffFetchOptions): Promise<UserStaff> {
    const { data } = await this.client.get<unknown>(`/userpermission/staff/${id}/userid`, { params: serializeCbiDto(options, "generated.dto.UserStaffFetchOptions") });
    return deserializeCbiDto<UserStaff>(data, "generated.dto.UserStaff");
  }

  async getUserPermissionStaffScopeId(id: number, options?: ScopesFetchOptions): Promise<ScopesStaff> {
    const { data } = await this.client.get<unknown>(`/userpermission/staff/${id}/scopeid`, { params: serializeCbiDto(options, "generated.dto.ScopesFetchOptions") });
    return deserializeCbiDto<ScopesStaff>(data, "generated.dto.ScopesStaff");
  }

  async getUserPermissionStaffAssignedBy(id: number, options?: UserStaffFetchOptions): Promise<UserStaff> {
    const { data } = await this.client.get<unknown>(`/userpermission/staff/${id}/assignedby`, { params: serializeCbiDto(options, "generated.dto.UserStaffFetchOptions") });
    return deserializeCbiDto<UserStaff>(data, "generated.dto.UserStaff");
  }
}

export const usePermissionApi = (): PermissionApi => new PermissionApiClient();
